import { useWorkspaceContext } from '@/contexts/workspaceContext';
import { Result } from '@/lib/type';
import { useQueryCPs, useQueryPublicCPs } from '@/pages/admin/settings/cloud_provider/hook';
import { CloudProviderT } from '@/pages/admin/settings/cloud_provider/types';
import { isCPDisabled } from '@/pages/admin/settings/cloud_provider/util';
import {
  CloudPlatform,
  getAvailablePlatforms,
  hasSupportedTGVersion,
  isTGVersionSupportedOnPlatform,
  normalizePlatform,
  SUPPORTED_PLATFORMS,
} from '@/pages/admin/settings/cloud_provider/CloudProviderCategory';
import {
  deleteDatabase,
  deleteWorkSpace,
  getFeatureFlag,
  getGroupMetrics,
  getMeta,
  getPrice,
  getWorkGroups,
  getWorkspaceOrgQuota,
  keepAliveWorkSpace,
  KeepAliveWorkSpaceResult,
  pauseWorkSpace,
  refreshWorkSpace,
  renameDatabase,
  resumeWorkSpace,
  updateWorkSpace,
  updateWorkGroup,
  getScheduledUpgradeWorkspaces,
} from '@/pages/workgroup/api';
import {
  DatabaseT,
  getWorkspaceTypePrice,
  Pricing,
  FeatureFlags,
  UpdateWorkspaceRequest,
  WorkGroupT,
  WorkSpaceMeta,
  WorkSpaceOrgQuota,
  WorkspaceT,
  GroupMetrics,
} from '@/pages/workgroup/type';
import { AxiosError } from 'axios';
import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient, UseQueryOptions, UseMutationOptions } from 'react-query';
import { axiosController } from '@/lib/network';

export function useQueryGetPricing() {
  return useQuery<Result<Pricing>, AxiosError>(['resource-pricing'], getPrice);
}

export function useQueryGetOrgQuota() {
  return useQuery<Result<WorkSpaceOrgQuota>, AxiosError>(['workspaceQuota'], getWorkspaceOrgQuota);
}

export function useQueryGetFeatureFlag() {
  return useQuery<
    Result<
      {
        feature_flag: FeatureFlags;
        org_id: string;
      }[]
    >,
    AxiosError
  >(['feature-float'], getFeatureFlag);
}

export function useQueryGetGroups(
  options?: Omit<UseQueryOptions<Result<WorkGroupT[]>, AxiosError>, 'queryKey' | 'queryFn'>
) {
  return useQuery(['groups'], getWorkGroups, options);
}

export function useMutationUpdateWorkspace(
  options?: Omit<
    UseMutationOptions<
      Result<WorkspaceT>,
      AxiosError,
      { group_id: string; space_id: string; org_id?: string; data: UpdateWorkspaceRequest }
    >,
    'mutationFn'
  >
) {
  const queryClient = useQueryClient();
  const { isAdminDashboardPage } = useWorkspaceContext();

  return useMutation<
    Result<WorkspaceT>,
    AxiosError,
    { group_id: string; space_id: string; org_id?: string; data: UpdateWorkspaceRequest }
  >(
    ({ group_id, space_id, org_id, data }) => {
      return updateWorkSpace(group_id, space_id, data, isAdminDashboardPage, org_id);
    },
    {
      onSuccess: async (_, { group_id, space_id, org_id, data }) => {
        queryClient.invalidateQueries(['groups']);
        queryClient.invalidateQueries(['group', group_id]);
        queryClient.invalidateQueries(['scheduled-upgrade-workspaces']);
        if (isAdminDashboardPage) {
          queryClient.invalidateQueries(['admin', 'workspace', 'detail', org_id, group_id, space_id]);
        }

        if (data.workspace_name) {
          queryClient.setQueryData<Result<WorkGroupT> | undefined>(['group', group_id], (oldData) => {
            if (!oldData?.Result) {
              return oldData;
            }
            let newData = {
              ...oldData,
              Result: {
                ...oldData.Result,
                workspaces: oldData.Result.workspaces.map((w) =>
                  w.workspace_id !== space_id
                    ? w
                    : ({
                        ...w,
                        name: data.workspace_name,
                      } as WorkspaceT)
                ),
              },
            };
            return newData;
          });
        }
      },
      ...options,
    }
  );
}

export function useMutationDeleteWorkspace() {
  const queryClient = useQueryClient();

  return useMutation<Result<void>, AxiosError, { group_id: string; space_id: string }>(
    ({ group_id, space_id }) => {
      return deleteWorkSpace(group_id, space_id);
    },
    {
      onSuccess: async (_, { group_id, space_id }) => {
        queryClient.invalidateQueries(['groups']);
        queryClient.invalidateQueries(['solutions', group_id]);
        queryClient.invalidateQueries(['quick_insights', group_id]);
        queryClient.invalidateQueries(['workspaceQuota']);
      },
    }
  );
}

export function useMutationPauseWorkspace() {
  const queryClient = useQueryClient();

  return useMutation<Result<void>, AxiosError, { group_id: string; space_id: string }>(
    ({ group_id, space_id }) => {
      return pauseWorkSpace(group_id, space_id);
    },
    {
      onSuccess: async (_, { group_id, space_id }) => {
        queryClient.invalidateQueries(['group', group_id]);
        queryClient.invalidateQueries(['groups']);

        // update workspace status to 'Pending'
        queryClient.setQueryData<Result<WorkGroupT[]> | undefined>(['groups'], (oldData) => {
          if (!oldData?.Result) {
            return oldData;
          }
          let newData = {
            ...oldData,
            Result: oldData.Result.map((group) =>
              group.workgroup_id !== group_id
                ? group
                : {
                    ...group,
                    workspaces: group.workspaces.map((w) =>
                      w.workspace_id !== space_id
                        ? w
                        : ({
                            ...w,
                            status: 'Pending',
                          } as WorkspaceT)
                    ),
                  }
            ),
          };
          return newData;
        });
      },
    }
  );
}

export function useMutationResumeWorkspace() {
  const queryClient = useQueryClient();

  return useMutation<Result<void>, AxiosError, { group_id: string; space_id: string }>(
    ({ group_id, space_id }) => {
      return resumeWorkSpace(group_id, space_id);
    },
    {
      onSuccess: async (_, { group_id }) => {
        queryClient.invalidateQueries(['group', group_id]);
        queryClient.invalidateQueries(['groups']);
      },
    }
  );
}

/**
 * Defers the auto-pause of a workspace the user is actively viewing.
 *
 * The new deadline is patched straight into the cached workgroup data rather than invalidating it:
 * this fires every few minutes while a user works, and a refetch of every workgroup on each ping
 * would be pure waste.
 */
export function useMutationKeepAliveWorkspace() {
  const queryClient = useQueryClient();

  return useMutation<Result<KeepAliveWorkSpaceResult>, AxiosError, { group_id: string; space_id: string }>(
    ({ group_id, space_id }) => {
      return keepAliveWorkSpace(group_id, space_id);
    },
    {
      onSuccess: (data, { group_id, space_id }) => {
        const auto_stop_after = data?.Result?.auto_stop_after;
        if (!auto_stop_after) {
          return;
        }

        const patchWorkspace = (workspace: WorkspaceT) =>
          workspace.workspace_id !== space_id ? workspace : { ...workspace, auto_stop_after };

        queryClient.setQueryData<Result<WorkGroupT[]> | undefined>(['groups'], (oldData) => {
          if (!oldData?.Result) {
            return oldData;
          }
          return {
            ...oldData,
            Result: oldData.Result.map((group) =>
              group.workgroup_id !== group_id ? group : { ...group, workspaces: group.workspaces.map(patchWorkspace) }
            ),
          };
        });

        queryClient.setQueryData<Result<WorkGroupT> | undefined>(['group', group_id], (oldData) => {
          if (!oldData?.Result) {
            return oldData;
          }
          return {
            ...oldData,
            Result: { ...oldData.Result, workspaces: oldData.Result.workspaces.map(patchWorkspace) },
          };
        });

        queryClient.setQueryData<Result<WorkspaceT> | undefined>(['space', space_id], (oldData) => {
          if (!oldData?.Result) {
            return oldData;
          }
          return { ...oldData, Result: { ...oldData.Result, auto_stop_after } };
        });
      },
    }
  );
}

export function useMutationRefreshWorkspace() {
  const queryClient = useQueryClient();

  return useMutation<Result<void>, AxiosError, { group_id: string; space_id: string }>(
    ({ group_id, space_id }) => {
      return refreshWorkSpace(group_id, space_id);
    },
    {
      onSuccess: async (_, { group_id, space_id }) => {
        queryClient.setQueryData<Result<WorkGroupT> | undefined>(['group', group_id], (oldData) => {
          if (!oldData?.Result) {
            return oldData;
          }
          let newData = {
            ...oldData,
            Result: {
              ...oldData.Result,
              workspaces: oldData.Result.workspaces.map((w) =>
                w.workspace_id !== space_id
                  ? w
                  : ({
                      ...w,
                      refresh_status: 'Exporting',
                    } as WorkspaceT)
              ),
            },
          };

          queryClient.invalidateQueries(['group', group_id]);
          return newData;
        });
      },
    }
  );
}

export function useMutationRenameDatabase() {
  const queryClient = useQueryClient();

  return useMutation<Result<void>, AxiosError, { group_id: string; db_id: string; database_name: string }>(
    ({ group_id, db_id, database_name }) => {
      return renameDatabase(group_id, db_id, database_name);
    },
    {
      onSuccess: async (_, { group_id, db_id, database_name }) => {
        queryClient.invalidateQueries(['groups']);
        queryClient.invalidateQueries(['group', group_id]);

        queryClient.setQueryData<Result<WorkGroupT> | undefined>(['group', group_id], (oldData) => {
          if (!oldData?.Result) {
            return oldData;
          }
          let newData = {
            ...oldData,
            Result: {
              ...oldData.Result,
              tg_databases: oldData.Result.tg_databases.map((d) =>
                d.database_id !== db_id
                  ? d
                  : ({
                      ...d,
                      name: database_name,
                    } as DatabaseT)
              ),
            },
          };
          return newData;
        });
      },
    }
  );
}

export function useMutationDeleteDatabase() {
  const queryClient = useQueryClient();

  return useMutation<Result<void>, AxiosError, { group_id: string; db_id: string }>(
    ({ group_id, db_id }) => {
      return deleteDatabase(group_id, db_id);
    },
    {
      onSuccess: async (_, { group_id, db_id }) => {
        queryClient.invalidateQueries(['groups']);

        queryClient.setQueryData<Result<WorkGroupT> | undefined>(['group', group_id], (oldData) => {
          if (!oldData?.Result) {
            return oldData;
          }
          let newData = {
            ...oldData,
            Result: {
              ...oldData.Result,
              tg_databases: oldData.Result.tg_databases.filter((d) => d.database_id !== db_id),
            },
          };
          return newData;
        });
      },
    }
  );
}

// Whether a workgroup can be created on the cloud provider.
export function isCPSelectable(cp: CloudProviderT) {
  return !isCPDisabled(cp.status) && !cp.unsupported;
}

/**
 * @param checkTGVersions - also treat platforms without a supported TigerGraph version as unavailable,
 * replacing their public cloud providers with a "Coming Soon" placeholder and marking their BYOC cloud
 * providers as `unsupported`. Only use it for creation flows: other callers rely on every public cloud
 * provider being present (e.g. to resolve pricing).
 */
export function useCloudProviders(
  filterFn?: (cp: CloudProviderT) => boolean,
  { checkTGVersions = false }: { checkTGVersions?: boolean } = {}
) {
  const publicCPsQ = useQueryPublicCPs();
  const privateCpsQ = useQueryCPs();
  const metaQ = useQuery<Result<WorkSpaceMeta>, AxiosError>(['workspaceMeta'], () => getMeta(), {
    enabled: checkTGVersions,
  });
  const loading = publicCPsQ.isLoading || privateCpsQ.isLoading || (checkTGVersions && metaQ.isLoading);

  const cps: CloudProviderT[] = useMemo(() => {
    const tgVersions = checkTGVersions ? metaQ.data?.Result?.tgVersions || [] : undefined;
    const allPublicCPs = publicCPsQ.data?.Result || [];
    // the org scoped list also returns the public providers of that org, so drop them here
    // and keep the entries from the public list as the single source for them
    const publicCPIds = new Set(allPublicCPs.map((cp) => cp.id));
    const cps = (privateCpsQ.data?.Result || [])
      .filter((cp) => !publicCPIds.has(cp.id))
      .map((cp) => (tgVersions && !hasSupportedTGVersion(cp.platform, tgVersions) ? { ...cp, unsupported: true } : cp));
    const availablePlatforms = getAvailablePlatforms(allPublicCPs, tgVersions);
    const publicCPs = allPublicCPs
      .filter((provider) => availablePlatforms.has(normalizePlatform(provider.platform)))
      .map((provider) => ({
        ...provider,
        type: 'public',
      }));
    const result = [...cps, ...publicCPs].filter(filterFn || (() => true)).sort((cp1, cp2) => {
      if (isCPSelectable(cp2) && !isCPSelectable(cp1)) {
        return 1;
      } else if (isCPSelectable(cp1) && !isCPSelectable(cp2)) {
        return -1;
      } else {
        return 0;
      }
    });

    const aws = publicCPs.find((cp) => normalizePlatform(cp.platform) === CloudPlatform.AWS);
    if (!aws) {
      return result;
    }
    // Show a "Coming Soon" placeholder for every platform without an available public cloud provider yet.
    const placeholders: CloudProviderT[] = SUPPORTED_PLATFORMS.filter((p) => !availablePlatforms.has(p)).map((p) => ({
      ...aws,
      id: p.toLowerCase(),
      platform: p.toLowerCase(),
      comingSoon: true,
    }));
    return [...result, ...placeholders];
  }, [privateCpsQ.data?.Result, publicCPsQ.data?.Result, metaQ.data?.Result?.tgVersions, checkTGVersions, filterFn]);

  return { cps, isCpLoading: loading };
}

// calculate the price of the workspace based on the [platform, region, cloud_provider_id]
// 1 get the price/cloud provider from the workspace context
// 2 calculate the cloud provider type (TGCloud or BYOC) based on the cloud_provider_id
export function useQueryWorkspaceMeta(platform: string, region: string, cloud_provider_id: string) {
  const { price, cps } = useWorkspaceContext();

  const { data, isLoading } = useQuery<Result<WorkSpaceMeta>, AxiosError>(['workspaceMeta'], () => getMeta());

  const result = useMemo(() => {
    if (data?.Result && platform && region && cloud_provider_id && price && cps.length > 0) {
      let { tgVersions, workspaceTypes, regions } = data.Result;
      tgVersions = tgVersions.filter((version) => isTGVersionSupportedOnPlatform(version.name, platform));
      const cloud_provider = cps.find((cp) => cp.id === cloud_provider_id)?.type === 'public' ? 'TGCloud' : 'BYOC';

      workspaceTypes = workspaceTypes.map((workspaceType) => {
        const cost_per_hour = getWorkspaceTypePrice(
          price,
          workspaceType.typeName,
          false,
          platform,
          region,
          cloud_provider
        );

        const cost_per_hour_with_ha = getWorkspaceTypePrice(
          price,
          workspaceType.typeName,
          true,
          platform,
          region,
          cloud_provider
        );

        return {
          ...workspaceType,
          cost_per_hour,
          cost_per_hour_with_ha,
        };
      });

      return {
        tgVersions,
        workspaceTypes,
        regions,
      };
    } else {
      return undefined;
    }
  }, [data?.Result, platform, region, cloud_provider_id, price, cps]);

  if (!price || cps.length === 0) {
    return {
      isLoading: true,
      data: undefined,
    };
  }

  if (isLoading) {
    return {
      isLoading: true,
      data: undefined,
    };
  }

  return {
    isLoading: false,
    data: result,
  };
}

export function useQueryUpgradeVersions() {
  const { data, isLoading } = useQuery<Result<WorkSpaceMeta>, AxiosError>(['workspaceMeta', 'upgrade'], () =>
    getMeta(true)
  );

  return {
    isLoading,
    tgVersions: data?.Result?.tgVersions ?? [],
  };
}

export function useQueryGetGroupMetrics(
  group_id: string,
  options?: Omit<UseQueryOptions<Result<GroupMetrics>, AxiosError>, 'queryKey' | 'queryFn'>
) {
  return useQuery(
    ['group_metrics', group_id],
    () => {
      return getGroupMetrics(group_id);
    },
    options
  );
}

export function useMutationUpdateWorkGroup() {
  const queryClient = useQueryClient();

  return useMutation<
    Result<void>,
    AxiosError,
    { group_id: string; workgroup_name: string; enable_application_log?: boolean }
  >(
    ({ group_id, workgroup_name, enable_application_log }) => {
      return updateWorkGroup(group_id, {
        workgroup_name,
        enable_application_log,
        ...(enable_application_log ? { encryption_method: 'TMK' } : {}),
      });
    },
    {
      onSuccess: async (data, { group_id, workgroup_name, enable_application_log }) => {
        queryClient.invalidateQueries(['groups']);
        queryClient.invalidateQueries(['group', group_id]);

        queryClient.setQueryData<Result<WorkGroupT> | undefined>(['group', group_id], (oldData) => {
          if (!oldData?.Result) {
            return oldData;
          }
          let newData = {
            ...oldData,
            Result: {
              ...oldData.Result,
              name: workgroup_name,
              enable_application_log: enable_application_log || oldData.Result.enable_application_log,
            },
          };
          return newData;
        });
      },
    }
  );
}

export function useLogFeatureEnabled() {
  const { featureFlags } = useWorkspaceContext();
  return (
    featureFlags?.some((f) => f.feature_name === 'encryption-at-rest' && f.enabled) &&
    featureFlags?.some((f) => f.feature_name === 'application-log' && f.enabled)
  );
}

export function useQueryScheduledUpgradeWorkspaces() {
  return useQuery<Result<WorkspaceT[]>, AxiosError>(['scheduled-upgrade-workspaces'], getScheduledUpgradeWorkspaces);
}

// Mutation to trigger workspace upgrade
export function useMutationUpgradeWorkspace(
  options?: Omit<
    UseMutationOptions<
      Result,
      AxiosError,
      { workgroup_id: string; workspace_id: string; org_id?: string; version?: string; scheduled?: boolean }
    >,
    'mutationFn'
  >
) {
  const queryClient = useQueryClient();
  const { isAdminDashboardPage } = useWorkspaceContext();

  return useMutation(
    async ({
      workgroup_id,
      workspace_id,
      org_id,
      version,
      scheduled,
    }: {
      workgroup_id: string;
      workspace_id: string;
      org_id?: string;
      version?: string;
      scheduled?: boolean;
    }) => {
      // Call the upgrade API for the workspace
      const res = await axiosController.post(
        `${
          isAdminDashboardPage ? `admin/orgs/${org_id!}` : 'v2'
        }/workgroups/${workgroup_id}/workspaces/${workspace_id}/upgrade`,
        version ? { desired_version: version, scheduled } : scheduled !== undefined ? { scheduled } : undefined
      );
      return res.data;
    },
    {
      onSuccess: (_, { workgroup_id }) => {
        // Invalidate relevant queries after upgrade
        queryClient.invalidateQueries(['scheduled-upgrade-workspaces']);
        queryClient.invalidateQueries(['groups']);
        queryClient.invalidateQueries(['group', workgroup_id]);
      },
      ...options,
    }
  );
}

// Mutation to trigger bulk workspace upgrades
export function useMutationBulkUpgradeWorkspaces(
  options?: Omit<
    UseMutationOptions<
      Result[],
      AxiosError,
      { workspaces: { workgroup_id: string; workspace_id: string; workgroup_name?: string; workspace_name?: string }[] }
    >,
    'mutationFn'
  >
) {
  const queryClient = useQueryClient();
  return useMutation(
    async ({
      workspaces,
    }: {
      workspaces: { workgroup_id: string; workspace_id: string; workgroup_name?: string; workspace_name?: string }[];
    }) => {
      // Upgrade all workspaces in parallel
      const upgradePromises = workspaces.map(({ workgroup_id, workspace_id }) =>
        axiosController.post(`v2/workgroups/${workgroup_id}/workspaces/${workspace_id}/upgrade`)
      );

      const results = await Promise.allSettled(upgradePromises);

      // Return results with success/error status
      return results.map((result, index) => {
        if (result.status === 'fulfilled') {
          return result.value.data;
        } else {
          // Return error result for failed upgrades with workspace/group names
          const workspace = workspaces[index];
          const workspaceDisplayName =
            workspace.workspace_name && workspace.workgroup_name
              ? `${workspace.workgroup_name}/${workspace.workspace_name}`
              : workspace.workspace_id;

          return {
            Error: true,
            ErrorDetails: result.reason,
            Message: `Failed to upgrade workspace ${workspaceDisplayName}`,
          };
        }
      });
    },
    {
      onSuccess: (results, { workspaces }) => {
        // Invalidate relevant queries after bulk upgrade
        queryClient.invalidateQueries(['scheduled-upgrade-workspaces']);
        queryClient.invalidateQueries(['groups']);

        // Invalidate specific group queries for affected workgroups
        const workgroupIds = [...new Set(workspaces.map((ws) => ws.workgroup_id))];
        workgroupIds.forEach((workgroup_id) => {
          queryClient.invalidateQueries(['group', workgroup_id]);
        });
      },
      ...options,
    }
  );
}
